import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  Typography,
} from "@mui/material";
import * as React from "react";
import { Advert } from "src/api/advert";

type AdvertCardProps = {
  advert: Advert;
};

const SITE_LABELS: Record<string, string> = {
  halo_oglasi: "Halo oglasi",
  nekretnine: "Nekretnine.rs",
  nadji_dom: "Nadji Dom",
  "4zida": "4zida",
  "4zida_API_V6": "4zida",
};

const formatPrice = (price: number) =>
  `${Math.round(price).toLocaleString("de-DE")} €`;

const metaLabel = (advert: Advert) => {
  const parts: string[] = [];

  if (advert.size > 0) {
    parts.push(`${advert.size} m²`);
  }

  if (advert.price > 0 && advert.size > 0) {
    parts.push(`${formatPrice(advert.price / advert.size)}/m²`);
  }

  return parts.join(" · ");
};

export const AdvertCard: React.FC<AdvertCardProps> = ({ advert }) => {
  const meta = metaLabel(advert);

  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        transition: "box-shadow .2s",
        "&:hover": { boxShadow: 3 },
      }}
    >
      <CardActionArea
        href={advert.url}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          justifyContent: "flex-start",
        }}
      >
        <CardMedia
          component="img"
          height="180"
          image={advert.img}
          alt={advert.title}
          sx={{ objectFit: "cover", bgcolor: "grey.100" }}
        />
        <CardContent sx={{ flexGrow: 1 }}>
          <Chip
            label={SITE_LABELS[advert.siteType] ?? advert.siteType}
            size="small"
            color="primary"
            variant="outlined"
            sx={{
              mb: 1,
              fontSize: 11,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: ".02em",
              borderRadius: 1,
            }}
          />
          <Typography
            variant="subtitle1"
            component="div"
            sx={{ fontWeight: 600, lineHeight: 1.4, mb: 1 }}
          >
            {advert.title}
          </Typography>
          {advert.price > 0 && (
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {formatPrice(advert.price)}
            </Typography>
          )}
          {meta && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {meta}
            </Typography>
          )}
          {advert.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {advert.description}
            </Typography>
          )}
        </CardContent>
      </CardActionArea>
      <CardActions
        sx={{ px: 2, pb: 2, justifyContent: "space-between" }}
      >
        <Box>
          <Typography variant="caption" color="text.secondary">
            {advert.scrapedAt.toLocaleString()}
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          href={advert.url}
          target="_blank"
          rel="noopener noreferrer"
          disableElevation
        >
          Pogledaj oglas →
        </Button>
      </CardActions>
    </Card>
  );
};
